import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * StraddleBreakout — Volatility Expansion Strategy
 *
 * How it works:
 * - Buys both ATM CE and ATM PE simultaneously (A Straddle).
 * - Ideal for severely compressed, range-bound markets waiting for a breakout.
 * - This strategy uses the `Total Count` metric to detect compression (e.g., Total Count is near zero).
 */
export const evaluateStraddleBreakout: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult[] | null => {
    if (!data) return null;

    const { symbol, expiry, gap } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length) return null;

    // Detect Compression via the Trend Analysis Data (Total Count)
    const trendAnalysisResult = context.liveIndicators?.trendAnalysisData;
    const fullTrendData = Array.isArray(trendAnalysisResult) ? trendAnalysisResult : (trendAnalysisResult?.data || []);
    
    // If we don't have enough data, wait.
    if (fullTrendData.length < 1) return null;

    // A low Total Count indicates neither bulls nor bears are strongly winning (Compression)
    const latestRow = fullTrendData[0];
    const totalCount = latestRow?.totalCount ?? 0;
    
    const isCompressed = Math.abs(totalCount) <= (config.aggressiveness || 5);

    if (!isCompressed) {
        return null;
    }

    const atmStrike = Math.round(spot / gap) * gap;

    // Find ATM CE Premium
    const ceRow = liveFlatOpts.find((r: any) => r.strike === atmStrike && r.type === 'CE');
    const cePremium = ceRow ? (ceRow.ltp || ceRow.close || ceRow.price || 0) : 0;

    // Find ATM PE Premium
    const peRow = liveFlatOpts.find((r: any) => r.strike === atmStrike && r.type === 'PE');
    const pePremium = peRow ? (peRow.ltp || peRow.close || peRow.price || 0) : 0;

    if (cePremium <= 0 || pePremium <= 0) return null;

    return [
        {
            trade_type: 'BULL', // Arbitrary for breakout, it's delta neutral
            strike: atmStrike,
            option_type: 'CE',
            entry_price: cePremium,
            remarks: `Straddle Breakout Long CE (Count: ${totalCount})`
        },
        {
            trade_type: 'BEAR', // Arbitrary for breakout, it's delta neutral
            strike: atmStrike,
            option_type: 'PE',
            entry_price: pePremium,
            remarks: `Straddle Breakout Long PE (Count: ${totalCount})`
        }
    ];
};
